import { FilePenLine, ChevronLeft, ChevronRight } from "lucide-react";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import type { TemplateMetaItem } from "../hooks/useCvState";
import { useSettings } from "../contexts/SettingsContext";
import { UserMenu } from "./UserMenu";

interface HeaderProps {
  theme: "light" | "dark";
  locale: "fr" | "en";
  isSidebarOpen: boolean;
  isAtsSidebarOpen: boolean;
  user: SupabaseUser | null;
  cvVersionCount?: number;
  templateMeta?: TemplateMetaItem[];
  activeTemplate?: string;
  setActiveTemplate?: (id: string) => void;
  onThemeToggle: () => void;
  onLocaleToggle: () => void;
  onSidebarToggle: () => void;
  onAtsSidebarToggle: () => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
}

export function Header({
  theme,
  locale,
  isSidebarOpen,
  isAtsSidebarOpen,
  user,
  cvVersionCount = 1,
  templateMeta,
  activeTemplate,
  setActiveTemplate,
  onThemeToggle,
  onLocaleToggle,
  onSidebarToggle,
  onAtsSidebarToggle,
  onLoginClick,
  onLogoutClick,
}: HeaderProps) {
  const { openSettings } = useSettings();

  return (
    <div className="hidden md:flex items-center gap-1 flex-1 min-w-0 justify-end">
      {/* Edition toggle */}
      <button
        type="button"
        onClick={onSidebarToggle}
        className={`flex items-center gap-1 px-3 py-2 rounded-xl border text-sm font-bold transition-all touch-manipulation min-h-[44px] items-center gap-1.5 ${
          isSidebarOpen
            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20 border-blue-600'
            : 'text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20'
        }`}
      >
        <FilePenLine size={14} className="shrink-0" aria-hidden="true" />
        {isSidebarOpen
          ? <><ChevronLeft size={12} aria-hidden="true" /> Edition</>
          : <><ChevronRight size={12} aria-hidden="true" /> Edition</>
        }
      </button>

      {/* CV version quick selector */}
      {templateMeta && templateMeta.length > 1 && activeTemplate && setActiveTemplate && (
        <select
          value={activeTemplate}
          onChange={(e) => setActiveTemplate(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white/80 font-semibold max-w-[120px] truncate"
          aria-label="CV version"
        >
          {templateMeta.map((tm) => (
            <option key={tm.id} value={tm.id}>{tm.name}</option>
          ))}
        </select>
      )}

      {/* Unified user menu */}
      <UserMenu
        user={user}
        cvVersionCount={cvVersionCount}
        theme={theme}
        locale={locale}
        isAtsSidebarOpen={isAtsSidebarOpen}
        onThemeToggle={onThemeToggle}
        onLocaleToggle={onLocaleToggle}
        onAtsSidebarToggle={onAtsSidebarToggle}
        onLoginClick={onLoginClick}
        onLogoutClick={onLogoutClick}
        onOpenSettings={openSettings}
      />
    </div>
  );
}
